<!-- TableHeader.svelte -->
<script lang="ts">
  import { cn } from "$lib/utils";
  import type { ColumnDefinition } from "./types";
  import { Checkbox } from "$lib/components/ui/checkbox";
  import { ArrowUpDown, ArrowUp, ArrowDown, SquareCheck } from "lucide-svelte";
  import * as Tooltip from "$lib/components/ui/tooltip";
  import { createEventDispatcher } from "svelte";
  const dispatch = createEventDispatcher();

  let {
    fixedColumns,
    dynamicColumns,
    columnConfigs,
    currentSortField = $bindable("name"),
    currentSortDirection = $bindable<"asc" | "desc">("asc"),
    isAllSelected = $bindable(false),
    showDefiniteOnly = $bindable(false),
    onSort,
    onSelectAll,
    onStartResize,
    isLoadingData,
  } = $props<{
    fixedColumns: ColumnDefinition[];
    dynamicColumns: ColumnDefinition[];
    columnConfigs: Record<string, ColumnDefinition>;
    currentSortField?: string;
    currentSortDirection?: "asc" | "desc";
    showDefiniteOnly?: boolean;
    isAllSelected?: boolean;
    isLoadingData?: boolean;
    onSort: (field: string) => void;
    onSelectAll: (checked: boolean) => void;
    onStartResize: (e: MouseEvent, columnKey: string, initialWidth: number) => void;
  }>();

  function handleDefiniteToggle() {
    showDefiniteOnly = !showDefiniteOnly;
  }
</script>

<thead class="sticky top-0 z-30 bg-inherit">
  <tr class="border-b border-theme-text-tertiary text-left text-xs dark:border-white/10">
    <!-- Fixed columns -->
    {#each fixedColumns as column, index}
      <th
        class={cn(
          "border-r border-theme-text-tertiary dark:border-white/10",
          "bg-gray-50 bg-theme-background-primary dark:bg-theme-background-primary",
          column.sticky ? `sticky left-${index * 12} z-40` : "",
          "box-border",
          "whitespace-nowrap",
        )}
        style="width: {column.width}px; min-width: {column.minWidth ?? column.width}px; max-width: {column.minWidth ??
          column.width}px;"
      >
        {#if column.key === "checkbox"}
          <div class="px-3">
            <Checkbox checked={isAllSelected} onCheckedChange={onSelectAll} class="!rounded-base" />
          </div>
        {:else}
          <span class="px-4 text-2xs">{column.label}</span>
        {/if}
      </th>
    {/each}

    <!-- Dynamic columns -->
    {#each dynamicColumns as column}
      <th
        class={cn(
          "relative border-r border-theme-text-tertiary px-4 py-3 font-medium dark:border-white/10",
          "bg-gray-50 dark:bg-theme-background-secondary",
          `text-${column.align}`,
          column.className,
          "box-border",
          "whitespace-nowrap",
        )}
        style="width: {columnConfigs[column.key].width}px;
            min-width: {columnConfigs[column.key].minWidth ?? columnConfigs[column.key].width}px;"
      >
        {#if column.sortable}
          <button class="inline-flex items-center gap-1 hover:text-theme-green-deep" onclick={() => onSort(column.key)}>
            {column.label}
            <span class="flex items-center">
              {#if currentSortField === column.key}
                {#if currentSortDirection === "asc"}
                  <ArrowUp class="h-3.5 w-3.5" />
                {:else}
                  <ArrowDown class="h-3.5 w-3.5" />
                {/if}
              {:else}
                <ArrowUpDown class="h-3.5 w-3.5" />
              {/if}
            </span>
          </button>
        {:else}
          <span>{column.label}</span>
        {/if}

        {#if column.key === "criteria"}
          <Tooltip.Provider>
            <Tooltip.Root>
              <Tooltip.Trigger class="absolute right-3 top-2.5" disabled={isLoadingData}>
                <button
                  class={cn(
                    "flex h-6 w-6 items-center justify-center p-0.5 transition-colors",
                    "hover:bg-theme-background-tertiary",
                    showDefiniteOnly ? "text-green-600 dark:text-theme-green" : "text-theme-text-tertiary",
                  )}
                  onclick={handleDefiniteToggle}
                  disabled={isLoadingData}
                  aria-label="Toggle definite matches"
                >
                  <SquareCheck strokeWidth={2} size={14} />
                </button>
              </Tooltip.Trigger>
              <Tooltip.Content
                class="!rounded-base px-2 py-1 text-2xs font-normal dark:border dark:border-white/10 dark:bg-theme-background-tertiary dark:text-theme-gray-600"
                side="bottom"
              >
                <p>Show only definite matches</p>
              </Tooltip.Content>
            </Tooltip.Root>
          </Tooltip.Provider>
        {/if}

        {#if column.resizable}
          <!-- svelte-ignore event_directive_deprecated -->
          <!-- svelte-ignore a11y_consider_explicit_label -->
          <!-- svelte-ignore a11y_no_interactive_element_to_noninteractive_role -->
          <button
            class="absolute right-0 top-0 h-full w-1 cursor-col-resize transition-colors hover:bg-theme-green-deep"
            onmousedown={(e) => onStartResize(e, column.key, columnConfigs[column.key].width)}
            role="separator"
            aria-orientation="vertical"
            tabindex="0"
          ></button>
        {/if}
      </th>
    {/each}
  </tr>
</thead>
